import { and, or } from 'tio-ui/utilities';
import { getStatus, getDisputeNote } from 'tio-common/helpers/observability';
import { htmlSafe } from '@ember/template';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { VStack, HStack, Header, Section } from 'tio-ui/components/layout';
import Component from '@glimmer/component';
import ExtractionSummary from 'tio-employee/components/observability/extraction-summary';
import TioPageBreadcrumbs from 'tio-common/components/tio/page/breadcrumbs';
import type IntlService from 'ember-intl/services/intl';
import type ObservableDocumentModel from 'tio-data/models/observable-document';
import { PhFileArrowUp, PhEnvelope } from 'ember-phosphor-icons';
import Chip from 'tio-reimagined/components/chip';
import type { ChipSignature } from 'tio-reimagined/components/chip';

interface RouteSignature {
  Args: {
    model: ObservableDocumentModel;
  };
}

const getStatusIntent = (status: string): ChipSignature['Args']['intent'] => {
  switch (status) {
    case 'PROCESSING':
      return 'primary';
    case 'INVALID':
    case 'FAILED':
    case 'NOT_APPLIED':
      return 'danger';
    case 'PROCESSED':
    case 'APPLIED':
    case 'PROXY':
      return 'success';
    case 'IN_DISPUTE':
    case 'PARTIALLY_APPLIED':
      return 'warning';
  }
};

const getSubmittedIcon = (via: string) => {
  if (via === 'EMAIL') {
    return PhEnvelope;
  }
  return PhFileArrowUp;
};

const formatDisputeNote = (note?: string): string | undefined => {
  if (note) {
    return `"${note}"`;
  }
};

export default class ObservabilityDocumentsShow extends Component<RouteSignature> {
  @service declare intl: IntlService;

  get documentCreated() {
    const observableDocument = this.args.model;
    const createdAtDate = new Date(observableDocument.createdAt);
    const date = createdAtDate.toLocaleDateString();
    const time = createdAtDate.toLocaleTimeString();
    const via = this.intl.t(`observability.${observableDocument.via}`);
    return { via, date, time };
  }

  get documentSuccessful() {
    return ['ExtractionState.PROCESSED', 'ExtractionState.PROXY'].includes(
      this.args.model?.extractionState
    );
  }

  get documentUnsuccessful() {
    return ['ExtractionState.FAILED', 'ExtractionState.INVALID'].includes(
      this.args.model?.extractionState
    );
  }

  get documentInReview() {
    return this.args.model?.subsistenceState === 'SubsistenceState.IN_DISPUTE';
  }

  get documentPartiallyApplied() {
    return this.args.model?.reflectionState === 'ReflectionState.PARTIALLY_APPLIED';
  }

  <template>
    <VStack>
      <Header>
        <TioPageBreadcrumbs as |b|>
          <b.crumb
            @route="authenticated.account-activity.accounts"
            @label={{t "accounts.my_accounts"}}
          />
          <b.crumb @label={{t "observability.document.summary.header"}} />
        </TioPageBreadcrumbs>
      </Header>
      <Section>
        <:header>
          <HStack class="justify-between">
            <hgroup>
              <h1 class="inline">{{@model.observableAsset.filename}}</h1>
              <Chip @intent={{getStatusIntent (getStatus @model)}} class="ml-4">
                {{t "observable_document.status" state=(getStatus @model)}}
              </Chip>
              <p class="italic flex items-center text-small font-normal mt-2">
                {{#let (getSubmittedIcon @model.via) as |SubmittedIcon|}}
                  <SubmittedIcon @size="20" class="mr-2" />
                {{/let}}
                {{t
                  "observability.dispute.document.uploaded"
                  via=this.documentCreated.via
                  date=this.documentCreated.date
                  time=this.documentCreated.time
                }}
              </p>
            </hgroup>
            <a
              href={{@model.observableAsset.signedUrl}}
              target="_blank"
              rel="noopener noreferrer"
              class="tio-btn tio-btn-default tio-btn-outlined ml-auto"
            >
              {{t "common.download"}}
            </a>
          </HStack>
        </:header>
        <:body>
          {{#if this.documentSuccessful}}
            <ExtractionSummary @document={{@model}} class="w-full md:w-2/3" />
            <footer>
              {{#if (or this.documentInReview this.documentPartiallyApplied)}}
                <p class="my-8">
                  {{#if (and this.documentInReview (getDisputeNote @model))}}
                    {{formatDisputeNote (getDisputeNote @model)}}
                  {{else if this.documentPartiallyApplied}}
                    {{t "observability.document.summary.partially_applied"}}
                  {{/if}}
                </p>
              {{/if}}
            </footer>
          {{else}}
            <p class="text-red-700 my-8">
              {{t "observability.documents.show.unsuccessful"}}
            </p>
            <p class="my-4">
              {{t "observability.uploads.support.prompt"}}
              <span class="block text-sm my-2">
                {{htmlSafe
                  (t
                    "observability.uploads.support.email"
                    email="support@tuition.io"
                    linkClass="tio-link"
                  )
                }}
              </span>
              <span class="block text-sm my-2">
                {{t "observability.uploads.support.phone"}}
              </span>
            </p>
          {{/if}}
        </:body>
      </Section>
    </VStack>
  </template>
}
